/**
 * TalkVibesTab — universal "<Subject> Talk – Vibes" discussion tab.
 *
 * Wraps the existing polymorphic <EntityMessageBoard> in a Facebook-style
 * shell that any FoodPulse page (product, business, city, article, food
 * item, deal, hotel, airport, attraction…) can drop in with two props:
 *
 *   <TalkVibesTab entityType="product" entityId="doritos-cool-ranch"
 *                 subject="Doritos Cool Ranch" />
 *
 * What it renders:
 *   1. A big header:  "<Subject> Talk – Vibes"
 *   2. Sub-copy inviting the visitor to post, react, or ask.
 *   3. Category chip row (All · Tip · Question · Help · Deal · Weather · …).
 *   4. The existing <EntityMessageBoard> filtered by the selected chip.
 *   5. Optional "Ask the community" and "Share a tip" buttons that pre-set
 *      the category and scroll the composer into focus.
 *
 * Categories map 1:1 to backend `post_type` values that we accept on the
 * `entity_messages` collection. We already extended the whitelist there,
 * so no additional API changes are required.
 */
import { useRef } from "react";
import { MessageCircle, Sparkles, HelpCircle } from "lucide-react";
import EntityMessageBoard from "@/components/EntityMessageBoard";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";

export default function TalkVibesTab({
  entityType,
  entityId,
  subject,
  intro,             // optional override for the sub-copy
  className = "",
}) {
  const composerRef = useRef(null);

  const focusComposer = () => {
    setTimeout(() => {
      const el = composerRef.current?.querySelector("textarea, input[name='title']");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus();
    }, 60);
  };

  return (
    <section className={`rounded-3xl border bg-white ${className}`} style={{ borderColor: "#E9D8B4" }} data-testid="talk-vibes-tab">
      <header className="p-6 border-b" style={{ borderColor: "#F5E6C4", background: "linear-gradient(135deg,#FEF3C7,#F0E2C7)" }}>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold" style={{ color: PURPLE }}>
          <MessageCircle size={14} /> Community · Talk – Vibes
        </div>
        <h2 className="text-2xl md:text-3xl font-black leading-tight mt-1" style={{ color: GREEN }} data-testid="talk-vibes-title">
          {subject} Talk – Vibes
        </h2>
        <p className="text-sm text-gray-800 mt-2 max-w-2xl leading-relaxed">
          {intro || `Ask a question, share a tip, drop a photo, warn about a delay, or help another visitor exploring ${subject}. Every post shows up here in real time.`}
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={focusComposer}
            className="rounded-full px-4 py-2 text-xs font-bold inline-flex items-center gap-1"
            style={{ background: GREEN, color: CREAM }}
            data-testid="share-a-tip-btn"
          >
            <Sparkles size={12} /> Share a tip
          </button>
          <button
            onClick={focusComposer}
            className="rounded-full px-4 py-2 text-xs font-bold inline-flex items-center gap-1"
            style={{ background: PURPLE, color: "white" }}
            data-testid="ask-community-btn"
          >
            <HelpCircle size={12} /> Ask the community
          </button>
        </div>
      </header>

      <div ref={composerRef} className="p-4">
        <EntityMessageBoard
          entityType={entityType}
          entityId={entityId}
          title=""
        />
      </div>
    </section>
  );
}
